/**
 * CameraManager -- state machine for webcam lifecycle management.
 *
 * Handles constraint negotiation, Safari quirks (playsinline), mirror mode,
 * and front/back camera switching.
 *
 * State machine transitions:
 *   idle -> requesting -> streaming <-> paused -> idle
 *   any  -> error
 */

import type { MediaPort } from './ports/media-port.js';
import { BrowserMediaPort } from './ports/browser-media-port.js';

export type CameraState = 'idle' | 'requesting' | 'streaming' | 'paused' | 'error';

/** Error thrown when camera access fails or is denied. */
export class CameraError extends Error {
  public readonly cause?: unknown

  constructor(
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.cause = cause;
    this.name = 'CameraError';
  }
}

/**
 * Ordered fallback constraint chain: try best quality first.
 *
 * Uses min/ideal/max ranges so the browser can pick the closest supported
 * resolution without throwing OverconstrainedError. Each tier narrows the
 * acceptable range while still allowing the UA to adapt.
 */
const CONSTRAINT_CHAIN: MediaTrackConstraints[] = [
  {
    width: { min: 640, ideal: 1280, max: 1920 },
    height: { min: 360, ideal: 720, max: 1080 },
    frameRate: { ideal: 30, max: 60 },
    facingMode: 'user',
  },
  {
    width: { min: 320, ideal: 640, max: 1280 },
    height: { min: 240, ideal: 480, max: 720 },
    frameRate: { ideal: 30 },
    facingMode: 'user',
  },
  { facingMode: 'user' },
  // Last resort: any video device, no preference
];

export class CameraManager {
  state: CameraState = 'idle';
  stream: MediaStream | null = null;
  readonly videoElement: HTMLVideoElement;

  /** Called whenever the camera state transitions. */
  onStateChange?: (state: CameraState) => void;

  /** Called whenever mirror state changes -- widget wires this to mirror the canvas. */
  onMirrorChange?: (mirrored: boolean) => void;

  /** Whether the video feed is horizontally mirrored (selfie mode). */
  private mirrored = true;
  /** 'user' (front) or 'environment' (back). */
  private facingMode: 'user' | 'environment' = 'user';

  /**
   * CSS class applied to the video element when mirrored.
   * Defaults to 'mb-mirrored'. Widgets can override to match their prefix.
   */
  private readonly mirrorClass: string;

  /** Port for browser API calls — injectable for testing, defaults to BrowserMediaPort. */
  private readonly mediaPort: MediaPort;

  /** Bound reference kept so the same function can be removed later. */
  private readonly _onVisibilityChange: () => void;

  constructor(options?: { mirrorClass?: string; mediaPort?: MediaPort }) {
    this.mirrorClass = options?.mirrorClass ?? 'mb-mirrored';
    this.mediaPort = options?.mediaPort ?? new BrowserMediaPort();
    this.videoElement = this.createVideoElement();
    this._onVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Start the camera stream.
   *
   * Attempts each constraint set in the fallback chain until one succeeds.
   * The final fallback is `{ video: true }` -- the bare minimum.
   * Throws CameraError if all attempts fail.
   *
   * @param constraints - Optional override constraints (skips the fallback chain).
   */
  async start(constraints?: MediaTrackConstraints): Promise<void> {
    if (this.state === 'streaming') return;
    this.setState('requesting');

    // Build the chain: either the single override or the full fallback chain,
    // each with the current facingMode applied (except the bare `{ video: true }` last resort).
    const chain: Array<MediaStreamConstraints> = constraints
      ? [{ video: constraints, audio: false }]
      : [
          ...CONSTRAINT_CHAIN.map((c) => ({
            video: { ...c, facingMode: this.facingMode } as MediaTrackConstraints,
            audio: false,
          })),
          // Bare last resort -- no facingMode, no size hints
          { video: true, audio: false },
        ];

    let lastError: unknown;
    for (const streamConstraints of chain) {
      try {
        const stream = await this.mediaPort.getUserMedia(streamConstraints);
        this.stream = stream;
        await this.attachStream();
        this.setState('streaming');
        return;
      } catch (err) {
        lastError = err;
        // NotAllowedError / PermissionDeniedError are hard stops -- the user
        // rejected the prompt. Lower-quality constraints won't help.
        if (err instanceof DOMException) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            break;
          }
        }
      }
    }

    this.setState('error');
    throw new CameraError(
      'Failed to access camera. Check permissions and hardware.',
      lastError,
    );
  }

  /** Pause the video element without stopping the underlying stream. */
  pause(): void {
    if (this.state !== 'streaming') return;
    this.videoElement.pause();
    this.setState('paused');
  }

  /** Resume a previously paused stream. */
  resume(): void {
    if (this.state !== 'paused') return;
    void this.videoElement.play();
    this.setState('streaming');
  }

  /** Stop all tracks and release the camera hardware. */
  stop(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }
    // Clear srcObject using the same compatibility path used in attachStream
    try {
      this.videoElement.srcObject = null;
    } catch {
      // Legacy fallback path
      this.videoElement.src = '';
    }
    this.setState('idle');
  }

  /**
   * Toggle between front and back cameras.
   * Stops the current stream and restarts with the new facing mode.
   */
  async switchCamera(): Promise<void> {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    // Mirror front camera; do not mirror rear camera
    this.mirrored = this.facingMode === 'user';
    this.applyMirror();
    this.stop();
    await this.start();
  }

  /**
   * Toggle mirror mode on/off.
   * By default mirror is enabled for front-facing (selfie) camera.
   */
  setMirror(enabled: boolean): void {
    this.mirrored = enabled;
    this.applyMirror();
  }

  /** Full teardown -- stop stream, remove visibility listener, remove video from DOM. */
  destroy(): void {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this.stop();
    if (this.videoElement.parentElement) {
      this.videoElement.remove();
    }
    // Ensure state is idle after full teardown
    this.setState('idle');
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private createVideoElement(): HTMLVideoElement {
    const video = document.createElement('video');
    // Required by Safari to play inline without triggering fullscreen on iOS
    video.setAttribute('playsinline', '');
    // Legacy webkit attribute for older iOS Safari (< 10)
    video.setAttribute('webkit-playsinline', '');
    video.muted = true;
    video.autoplay = true;
    return video;
  }

  private applyMirror(): void {
    this.videoElement.style.transform = this.mirrored ? 'scaleX(-1)' : '';
    // Also toggle a class so external CSS selectors / siblings can match
    this.videoElement.classList.toggle(this.mirrorClass, this.mirrored);
    // Notify the widget so it can mirror the canvas overlay too
    try {
      this.onMirrorChange?.(this.mirrored);
    } catch {
      // Never let a subscriber crash the camera
    }
  }

  /**
   * Attach the current stream to the video element and wait until the video
   * is ready to render frames.
   *
   * Safari note: `videoWidth` / `videoHeight` may be 0 until the first frame
   * is decoded, even after `loadedmetadata`. We wait for `canplay` which fires
   * reliably once the browser has enough data to start rendering.
   * The `playing` event is still fine on Chrome/Firefox but Safari sometimes
   * fires it before dimensions are available, so `canplay` is safer.
   */
  private async attachStream(): Promise<void> {
    const video = this.videoElement;

    // Apply mirror for front camera before stream is visible
    this.applyMirror();

    // Set the stream source -- use srcObject where available, with a legacy
    // fallback for browsers (very old iOS Safari) that only support .src URLs.
    try {
      video.srcObject = this.stream;
    } catch {
      // Last-resort fallback: create an object URL from the stream.
      // This path is only hit in extremely old browsers.
      if (this.stream) {
        // MediaStream is accepted at runtime but TypeScript's lib.dom.d.ts
        // only lists Blob | MediaSource. Cast to satisfy the compiler.
        video.src = URL.createObjectURL(this.stream as unknown as Blob);
      }
    }

    // Wait until the browser has enough data to play at least one frame.
    // We listen to `canplay` instead of `playing` because Safari on iOS
    // sometimes does not fire `playing` when autoplay is blocked.
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('error', onError);
      };

      // loadedmetadata fires first -- use it to call play() which is required
      // on some browsers to actually start decoding frames after srcObject is set.
      const onLoadedMetadata = () => {
        void video.play().catch(reject);
      };

      const onCanPlay = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        reject(new CameraError('Video element failed to play.'));
      };

      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('error', onError, { once: true });

      // Kick off play immediately too -- in case loadedmetadata already fired
      // (e.g. stream was reused) or the browser needs an explicit play call.
      void video.play().catch(() => {
        // Ignore here; the error listener above handles fatal failures.
        // Autoplay policy errors will be surfaced via the onError path.
      });
    });
  }

  private setState(next: CameraState): void {
    if (this.state === next) return;
    this.state = next;
    try {
      this.onStateChange?.(next);
    } catch {
      // Never let a subscriber crash the state machine
    }
  }

  /** Pause when the tab goes hidden; resume when it becomes visible again. */
  private handleVisibilityChange(): void {
    if (document.hidden) {
      if (this.state === 'streaming') {
        this.pause();
      }
    } else {
      if (this.state === 'paused') {
        this.resume();
      }
    }
  }
}
